import React, { useEffect, useState } from "react";
import { BarChart3, Loader2, DollarSign, Percent, Wallet, Eye, TrendingUp, TrendingDown, Download, Tag } from "lucide-react";
import { toast } from "sonner";
import { MKT_GOLD } from "../../lib/marketplace";
import { apiEstadisticas, apiEstadisticasCsv } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { PeriodoSelector, StatKpi, SerieBars, Ranking, rangoDesde, soles, PRESETS } from "./statsui";

export default function EstadisticasVendedor() {
  const [preset, setPreset] = useState("30d");
  const [data, setData] = useState(undefined);
  const [bajando, setBajando] = useState(false);

  useEffect(() => {
    const dias = PRESETS.find((p) => p.key === preset).dias;
    const { desde, hasta } = rangoDesde(dias);
    setData(undefined);
    apiEstadisticas(desde, hasta).then(setData).catch(() => setData(null));
  }, [preset]);

  const exportar = async () => {
    setBajando(true);
    try {
      const dias = PRESETS.find((p) => p.key === preset).dias;
      const { desde, hasta } = rangoDesde(dias);
      await apiEstadisticasCsv(desde, hasta);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setBajando(false); }
  };

  const delta = data?.comparacion?.delta_ingreso_pct;

  return (
    <div className="space-y-6" data-testid="estadisticas-vendedor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2"><BarChart3 size={24} style={{ color: MKT_GOLD }} /> Estadísticas</h1>
          <p className="text-sm text-slate-500 mt-0.5">Rendimiento de tu tienda por periodo.</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodoSelector value={preset} onChange={setPreset} />
          <button onClick={exportar} disabled={bajando} data-testid="exportar-csv" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-100 disabled:opacity-60">
            {bajando ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} CSV
          </button>
        </div>
      </div>

      {data === undefined ? (
        <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando…</div>
      ) : !data ? (
        <p className="text-slate-400 py-10">No se pudo cargar las estadísticas.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatKpi Icon={TrendingUp} label="Ventas" value={data.ventas} color="#16A34A" />
            <StatKpi Icon={DollarSign} label="Ingreso bruto" value={soles(data.ingreso_bruto)} color="#2563EB"
              sub={delta != null ? `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta)}% vs. periodo anterior` : "sin comparación"} />
            <StatKpi Icon={Percent} label="Comisión pagada" value={soles(data.comision)} color="#DC2626" />
            <StatKpi Icon={Wallet} label="Ganancia neta" value={soles(data.ganancia_neta)} color={MKT_GOLD} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatKpi Icon={Eye} label="Visitas a tienda" value={data.visitas} color="#7C3AED" />
            <StatKpi Icon={TrendingUp} label="Tasa de conversión" value={data.conversion != null ? `${data.conversion}%` : "—"} color="#0891B2"
              sub={data.conversion == null ? "datos insuficientes" : null} />
          </div>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
            <h3 className="font-bold text-slate-900 mb-3">Ingresos por día</h3>
            <SerieBars serie={data.serie} />
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Ranking titulo="Más vendidos" items={data.top_items} label={(it) => it.titulo} valor={(it) => `${it.ventas} vta(s)`} />
            <Ranking titulo="Rendimiento por categoría" items={data.por_categoria} label={(it) => it.categoria} valor={(it) => soles(it.ingreso)} />
          </div>
        </>
      )}
    </div>
  );
}
